import {ArrayAttribute, ArrayAttributeOptions} from './ArrayAttribute';

/** A status offered in the column filter of a status history attribute */
export interface StatusHistoryOption {
  /** Value stored in the status column of the history table */
  id: string;
  /** Translation key used as label in the filter list */
  key: string;
  /** Icon shown next to the label, mirroring the one displayed in the column */
  icon?: string | null;
}

export type StatusHistoryAttributeOptions = ArrayAttributeOptions & {
  filterTable?: string | null;
  filterColumn?: string | null;
  filterOptions?: StatusHistoryOption[];
};

/**
 * Attribute for a one to many status history (e.g. `transaction_log`).
 * Its column filter allows to require statuses and to exclude others at the
 * same time (for example: validated but not paid). Required statuses are
 * combined with AND, excluded statuses must all be absent from the history.
 */
export class StatusHistoryAttribute extends ArrayAttribute {
  filterTable: string | null;
  filterColumn: string;
  filterOptions: StatusHistoryOption[];

  constructor(options: StatusHistoryAttributeOptions = {}) {
    super(options);
    this.filterTable = options.filterTable ?? null;
    this.filterColumn = options.filterColumn ?? 'status';
    this.filterOptions = options.filterOptions ?? [];
  }

  // Input type that is used if attribute is used in edit mode
  get inputType(): string {
    return 'status_history';
  }

  get customTableProperties(): Object {
    return {statusHistoryOptions: this.filterOptions};
  }

  /** Embed alias requiring the presence of the status at `index` in the history */
  includeEmbedAlias(index: number): string {
    return `${this.name}Inc${index}`;
  }

  /** Embed alias used to exclude all the forbidden statuses at once */
  get excludeEmbedAlias(): string {
    return `${this.name}Exc`;
  }

  /** Inner join embed: the parent row is dropped when the status is missing */
  includeEmbedSelectString(index: number): string {
    return `${this.includeEmbedAlias(index)}:${this.filterTable}!inner(${
      this.filterColumn
    })`;
  }

  /** Left join embed, combined with an `is.null` filter to build an anti join */
  get excludeEmbedSelectString(): string {
    return `${this.excludeEmbedAlias}:${this.filterTable}(${this.filterColumn})`;
  }
}
